export type ToastSeverity = "success" | "info" | "warning" | "error";

/**
 * Dwell time per toast category, in milliseconds. `null` never auto-hides.
 *
 * Call sites never pass milliseconds. The category comes from the façade method
 * plus at most one named option (see `resolveToastCategory`), so the whole app
 * retimes from this one table.
 */
export const ToastDuration = {
  /** Passive success or info with no next step. */
  CONFIRM: 4000,
  /** Success or info carrying a call to action. */
  ACTIONABLE: 10000,
  /** The window in which an `undo` toast can still be reversed. */
  UNDO: 8000,
  /** Any warning or error. */
  FAILURE: 8000,
  /** An app-level notice with no other home. Stays until dismissed. */
  STICKY: null,
} as const;

export type ToastDurationCategory = keyof typeof ToastDuration;

export type ToastCategoryInputs = {
  sticky?: boolean;
  undo?: unknown;
  action?: unknown;
};

/** Resolution order: sticky, undo, action, then severity. */
export const resolveToastCategory = (
  severity: ToastSeverity,
  { sticky, undo, action }: ToastCategoryInputs = {},
): ToastDurationCategory => {
  if (sticky) return "STICKY";
  if (undo) return "UNDO";
  if (action) return "ACTIONABLE";
  if (severity === "warning" || severity === "error") return "FAILURE";
  return "CONFIRM";
};

export type ResolvedToastDuration = {
  category: ToastDurationCategory;
  /** Milliseconds the toast stays, or `null` for a sticky toast. */
  duration: number | null;
};

export const resolveToastDuration = (
  severity: ToastSeverity,
  options: ToastCategoryInputs = {},
): ResolvedToastDuration => {
  const category = resolveToastCategory(severity, options);
  return { category, duration: ToastDuration[category] };
};
